import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

import { DocumentCountsData } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

/** Card config drives both visual styling and the value pulled from the API data. */
interface CountCard {
    label: string;
    description: string;
    iconSrc: string;
    iconBg: string;
    /** Key on `DocumentCountsData` that supplies the count for this card. */
    dataKey: keyof DocumentCountsData;
}

/**
 * Document Counts Widget
 *
 * Stacked actionable-document cards (Overdue, Pending Approvals, Pending
 * Signatures). Template iterates `cards` (static config array) and calls
 * `valueFor(card)` to read the live count from the data signal.
 *
 * Reads state from `WorkspaceOverviewStateService`.
 *
 * Tooltip: "Displays a summary of documents that require attention".
 */
@Component({
    selector: 'app-document-counts-widget',
    standalone: true,
    imports: [CommonModule, TooltipModule],
    templateUrl: './document-counts-widget.component.html',
    styleUrl: './document-counts-widget.component.scss',
})
export class DocumentCountsWidgetComponent implements OnInit {
    readonly data = signal<DocumentCountsData | null>(null);
    readonly loading = signal(false);

    /**
     * Static card config — styling and labels live here; counts come from
     * `data()` via `valueFor()`. Defined as a plain array so the template can
     * iterate it with `*ngFor` without calling `()`.
     */
    readonly cards: CountCard[] = [
        {
            label: 'Overdue documents',
            description: 'Across your workspace and projects',
            iconSrc: 'icons/icon-overdue-documents.svg',
            iconBg: '#FEE2E2',
            dataKey: 'overdue',
        },
        {
            label: 'Pending approvals',
            description: 'Across your workspace and projects',
            iconSrc: 'icons/icon-pending-approvals.svg',
            iconBg: '#E0F4FB',
            dataKey: 'pendingApprovals',
        },
        {
            label: 'Pending signatures',
            description: 'Across your workspace and projects',
            iconSrc: 'icons/icon-pending-signatures.svg',
            iconBg: '#EDEAFC',
            dataKey: 'pendingSignatures',
        },
    ];

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
        private readonly router: Router,
    ) {
        /** Re-fetch whenever mode changes. Service only accepts { mode }. */
        effect(() => {
            this.fetch(this.state.viewMode());
        });
    }

    ngOnInit(): void { /* initial fetch triggered by the effect above */ }

    /** Returns the count for `card` from the current data snapshot. */
    valueFor(card: CountCard): number {
        return this.data()?.[card.dataKey] ?? 0;
    }

    openCard(card: CountCard): void {
        this.router.navigate(['/documents'], { queryParams: { actionFilter: card.dataKey } });
    }

    private fetch(mode: ReturnType<WorkspaceOverviewStateService['viewMode']>): void {
        this.loading.set(true);
        this.service.getDocumentCounts({ mode }).subscribe({
            next: data => { this.data.set(data); this.loading.set(false); },
            error: ()   => this.loading.set(false),
        });
    }
}
